import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ConfirmDialog, ErrorState, ReadOnlyBanner } from '@bb/ui';
import { ApiError, type GlobalControlView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { ExportDialog } from '../exports/ExportDialog.tsx';
import { TagChecklist } from './TagChecklist.tsx';

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');
const when = (iso: string) => `${iso.slice(0, 16).replace('T', ' ')} UTC`;
const ranges = (r: Array<[number, number]>) => r.map(([a, b]) => `${a}–${b}`).join(', ');

/**
 * Settings → Global Control Group (BB-49): a fixed 1–15 % of users (random bucket ranges) that receives no messages,
 * to measure the lift of messaging as a whole. A group is never changed: turning it off and creating a new one picks
 * different users, and the old group stays in the history.
 */
export function GlobalControlPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/global-control' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/global-control' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canEdit = has('env.manage');
  const key = ['global-control', environmentId];
  const control = useQuery({ queryKey: key, queryFn: () => api.globalControl(environmentId) });
  const [percent, setPercent] = useState(10);
  const [exempt, setExempt] = useState<string[]>([]);
  const [confirm, setConfirm] = useState<'enable' | 'disable' | null>(null);
  const [exporting, setExporting] = useState(false);
  const set = (view: GlobalControlView) => {
    queryClient.setQueryData(key, view);
    setConfirm(null);
  };
  const enable = useMutation({ mutationFn: () => api.enableGlobalControl(environmentId, { percent, exemptTagIds: exempt }), onSuccess: set });
  const disable = useMutation({ mutationFn: () => api.disableGlobalControl(environmentId), onSuccess: set });

  if (control.isError) return <ErrorState title="The control group could not be loaded" message={errorText(control.error)} onRetry={() => void control.refetch()} />;
  if (!control.data) return <p aria-busy="true">Loading…</p>;
  const active = control.data.active;
  return (
    <section>
      <h1>Global Control Group</h1>
      <p className="hint">A random share of users that receives no campaign or Canvas messages, to measure what messaging adds. Transactional messages and those with an exempt tag still reach them.</p>
      {!canEdit && <ReadOnlyBanner>The control group is changed by admins (env.manage).</ReadOnlyBanner>}
      {active ? (
        <section aria-label="Active group">
          <p>
            <strong>{active.percent}%</strong> of users (random buckets {ranges(active.bucketRanges)}) since {when(active.enabledAt)}
            {active.createdBy?.email ? ` by ${active.createdBy.email}` : ''}. Exempt tags: {active.exemptTagIds.length}.
          </p>
          {canEdit && (
            <Button variant="danger" onClick={() => setConfirm('disable')}>
              Turn off
            </Button>
          )}{' '}
          {has('data.export') && (
            <Button variant="ghost" onClick={() => setExporting(true)}>
              Export members
            </Button>
          )}
          <ExportDialog environmentId={environmentId} target={{ kind: 'global_control' }} title="Export members of the control group" open={exporting} onOpenChange={setExporting} />
        </section>
      ) : (
        canEdit && (
          <section aria-label="Turn on">
            <label>
              Share of users
              <select value={percent} onChange={(e) => setPercent(Number(e.target.value))}>
                {Array.from({ length: 15 }, (_, i) => i + 1).map((p) => (
                  <option key={p} value={p}>
                    {p}%
                  </option>
                ))}
              </select>
            </label>
            <TagChecklist environmentId={environmentId} label="Exempt tags: messages with any of these still reach the group" value={exempt} onChange={setExempt} />
            <p className="notice notice--warning">A group cannot be changed once on. To change it, turn it off and turn on a new one — that picks different users.</p>
            <Button onClick={() => setConfirm('enable')}>Turn on</Button>
          </section>
        )
      )}
      {(enable.isError || disable.isError) && <p role="alert">{errorText(enable.error ?? disable.error)}</p>}
      {control.data.history.length > 0 && (
        <table className="table" aria-label="History">
          <thead>
            <tr>
              <th scope="col">Share</th>
              <th scope="col">Buckets</th>
              <th scope="col">On</th>
              <th scope="col">Off</th>
            </tr>
          </thead>
          <tbody>
            {control.data.history.map((g) => (
              <tr key={g.id}>
                <td>{g.percent}%</td>
                <td>{ranges(g.bucketRanges)}</td>
                <td>{when(g.enabledAt)}</td>
                <td>{g.disabledAt ? when(g.disabledAt) : 'active'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <ConfirmDialog
        open={confirm !== null}
        onOpenChange={(open) => !open && setConfirm(null)}
        title={confirm === 'enable' ? `Turn on a ${percent}% control group?` : 'Turn off the control group?'}
        confirmLabel={confirm === 'enable' ? 'Turn on' : 'Turn off'}
        busy={enable.isPending || disable.isPending}
        onConfirm={() => (confirm === 'enable' ? enable.mutate() : disable.mutate())}
      >
        {confirm === 'enable' ? 'These users stop receiving messages right away. The group cannot be changed later, only turned off.' : 'Its users start receiving messages again. A new group will pick different users.'}
      </ConfirmDialog>
    </section>
  );
}
